import React from 'react';
import request from 'superagent';
import './../ff001-starstruck-lines/ff001-starstruck.css';
import './style.css';
import { wrap } from '../../helpers';
import { Reel } from '../reel/reel';
import {
  addOrReplaceEvent,
  applyEvent,
  applyCard,
  baseCoinValuesAreForced,
  canAddEventAt,
  cellKey,
  cellOrder,
  clone,
  createBlankState,
  createSuperContinuation,
  deserializeScenarioQueue,
  editorTriggersHold,
  findReelIndexes,
  isRandomBonusEligible,
  TFf036SearchKind,
  getBaseGrowthEligibleCells,
  getBaseJokerFeasibleVariants,
  getConfiguredJackpots,
  getConfiguredPrizeMultipliers,
  getValidOutcomes,
  getVisibleCoinCells,
  getVisibleVortexCells,
  getVisibleWindow,
  getHoldJokerFeasibleVariants,
  getSuperContinuationWindow,
  isSuperPsychoForceEligible,
  projectFeatureSteps,
  reconcileBaseCoins,
  reconcileFeatureEvidence,
  reconcileGrowthCells,
  reconcileJokerControl,
  reconcileSuperContinuation,
  serializeScenarioQueue,
  superContinuationSatisfiesAward,
  toggleCell,
  TFf036Board,
  TFf036BoardCell,
  TFf036CoinOverlay,
  TFf036Config,
  TFf036EditorState,
  TFf036FeatureCard,
  TFf036FeatureStep,
  TFf036JokerControl,
  TFf036JokerVariant,
  TFf036Jackpot,
  TFf036Scenario,
} from './helpers';

type TPreset = { _id: string; scenarios: TFf036Scenario[] };

export const responseErrorMessage = (body: any, fallback: string) =>
  body?.error?.message || body?.message || (typeof body?.error === 'string' ? body.error : '') || fallback;

const requestErrorMessage = (error: any, fallback: string) =>
  responseErrorMessage(error?.response?.body, error?.message || fallback);

const BaseBoard = (props: {
  window: string[][];
  coinOverlays: TFf036CoinOverlay[];
  config: TFf036Config;
  ariaLabel: string;
  coinLabel: string;
  growthCells?: Array<{ reel: number; row: number }>;
  growthEligibleCells?: Array<{ reel: number; row: number }>;
  coinEditingEnabled?: boolean;
  onCoinChange: (cell: { reel: number; row: number }, value: string) => void;
  onGrowthToggle?: (cell: { reel: number; row: number }) => void;
}) => {
  const overlays = new Map(props.coinOverlays.map(coin => [cellKey(coin), coin]));
  const growth = new Set(props.growthCells?.map(cellKey));
  const growthEligible = new Set(props.growthEligibleCells?.map(cellKey));
  const prizes = getConfiguredPrizeMultipliers(props.config);
  const jackpots = getConfiguredJackpots(props.config);
  return (
    <div className="ff036-base-board" aria-label={props.ariaLabel}>
      {props.window.map((reel, reelIndex) => (
        <div className="ff036-board-reel" key={`base-${reelIndex}`}>
          {reel.map((symbol, row) => {
            const cell = { reel: reelIndex, row };
            const overlay = overlays.get(cellKey(cell));
            return (
              <div className={`ff036-base-cell ff036-symbol-${symbol}`} key={cellKey(cell)}>
                <span className="ff036-cell-coordinate">R{reelIndex + 1} · {row + 1}</span>
                {symbol === props.config.coinFeature.coin ? (
                  <>
                    <strong>Coin</strong>
                    <select
                      aria-label={`${props.coinLabel} Coin ${reelIndex}-${row}`}
                      disabled={props.coinEditingEnabled === false}
                      value={overlay?.coinType === 'price' ? `${overlay.multiplier}` : overlay?.coinType || ''}
                      onChange={event => props.onCoinChange(cell, event.target.value)}
                    >
                      {!overlay && <option value="">Natural (server-selected)</option>}
                      {prizes.map(value => <option value={value} key={value}>{value}x Prize</option>)}
                      {jackpots.map(value => <option value={value} key={value}>{value.toUpperCase()}</option>)}
                    </select>
                  </>
                ) : symbol === 'vortex' ? <strong>Vortex</strong> : <span>{symbol}</span>}
                {props.growthCells !== undefined && props.onGrowthToggle && growthEligible.has(cellKey(cell)) && (
                  <label className="ff036-growth-toggle">
                    <input
                      type="checkbox"
                      aria-label={`${props.coinLabel} perceived growth ${reelIndex}-${row}`}
                      checked={growth.has(cellKey(cell))}
                      onChange={() => props.onGrowthToggle!(cell)}
                    />
                    Perceived growth +1
                  </label>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const boardCellLabel = (cell: TFf036BoardCell) => {
  if (cell.coin) {
    return cell.coin.coinType === 'price'
      ? `${cell.coin.originalMultiplier}x Prize Coin`
      : cell.coin.coinType.toUpperCase();
  }
  if (cell.vortex) {
    return cell.vortex.storedBaseMultiplier > 0
      ? `Vortex · ${cell.vortex.storedBaseMultiplier}x stored base`
      : 'Vortex';
  }
  return cell.storedCellMultiplier > 0 ? `Empty · +${cell.storedCellMultiplier}x stored` : 'Empty';
};

const FeatureBoard = (props: {
  board: TFf036Board;
  selected?: { reel: number; row: number };
  onSelect: (cell: { reel: number; row: number }) => void;
}) => (
  <div className="ff036-feature-board" aria-label="Hold and Win board">
    {props.board.map((reel, reelIndex) => (
      <div className="ff036-board-reel" key={`feature-${reelIndex}`}>
        {reel.map(cell => {
          const multiplier = cell.coin?.appliedCellMultiplier || cell.vortex?.appliedCellMultiplier || cell.storedCellMultiplier;
          const selected = props.selected && cellKey(props.selected) === cellKey(cell);
          return (
            <button
              type="button"
              aria-label={`Select cell ${cell.reel}-${cell.row}: ${boardCellLabel(cell)}`}
              className={`ff036-feature-cell ${selected ? 'selected' : ''} ${multiplier ? 'multiplied' : ''}`}
              data-cell-multiplier={multiplier || undefined}
              key={cellKey(cell)}
              onClick={() => props.onSelect({ reel: cell.reel, row: cell.row })}
            >
              <span className="ff036-cell-coordinate">R{cell.reel + 1} · {cell.row + 1}</span>
              <strong>{boardCellLabel(cell)}</strong>
              {multiplier > 0 && <span className="ff036-multiplier-border">+{multiplier}x border</span>}
            </button>
          );
        })}
      </div>
    ))}
  </div>
);

const JOKER_LABELS: Record<TFf036JokerVariant, string> = {
  addPrizeCoin: 'Add Prize Coin',
  addVortex: 'Add Vortex',
  boostPrizeCoin: 'Boost Prize Coin',
  completeCollection: 'Complete collection',
  completeBonusTrigger: 'Complete bonus trigger',
  addCellMultiplier: 'Add cell multiplier',
};

const preserveUnknownJokerFields = (
  current: TFf036JokerControl | undefined,
  next: TFf036JokerControl | undefined,
): TFf036JokerControl | undefined => {
  if (!next) return undefined;
  const preserved = Object.fromEntries(Object.entries(current || {}).filter(([key]) =>
    !['version', 'activation', 'variant', 'target', 'prizeMultiplier'].includes(key)));
  return { ...preserved, ...next } as TFf036JokerControl;
};

const holdSeedValuesAreForced = (state: TFf036EditorState): boolean => {
  const superOwnsFeatureSeed = state.base.superPsychoRespin?.activation === 'activate' &&
    state.base.superPsychoRespin.awardClass === 'hold-and-win' && !!state.superContinuation;
  return superOwnsFeatureSeed
    ? !!state.superContinuation?.coinOverlaysPresent && !!state.superContinuation.reelIndexes
    : baseCoinValuesAreForced(state.base);
};

export const JokerControls = (props: {
  label: string;
  control?: TFf036JokerControl;
  feasible: Array<{ variant: TFf036JokerVariant; kind?: string; targets: Array<{ reel: number; row: number }> }>;
  config: TFf036Config;
  onChange: (control?: TFf036JokerControl) => void;
}) => {
  const activation = props.control?.activation || 'natural';
  const selected = props.feasible.find(item => item.variant === props.control?.variant);
  const supportsPrize = selected?.kind === 'add-prize' || selected?.variant === 'addPrizeCoin';
  const disabled = props.config.jokerIntervention?.enabled === false;
  const canForce = !disabled && props.feasible.length > 0;
  return (
    <fieldset className="ff036-control-panel">
      <legend>{props.label} Joker Intervention</legend>
      {disabled && <p>Joker Intervention is disabled by this configuration.</p>}
      {!disabled && !canForce && <p>No Joker variant is feasible for the current window.</p>}
      <label>
        Behavior
        <select
          aria-label={`${props.label} Joker activation`}
          value={activation}
          onChange={event => {
            const value = event.target.value;
            props.onChange(value === 'natural' ? undefined : {
              version: 1,
              activation: value as 'activate' | 'skip',
            });
          }}
        >
          <option value="natural">Natural (server-selected)</option>
          {!disabled && <option value="activate" disabled={!canForce}>Force activation</option>}
          <option value="skip">Force skip</option>
        </select>
      </label>
      {activation === 'activate' && canForce && (
        <>
          <label>
            Variant
            <select
              aria-label={`${props.label} Joker variant`}
              value={props.control?.variant || ''}
              onChange={event => props.onChange({
                version: 1,
                activation: 'activate',
                ...(event.target.value ? { variant: event.target.value as TFf036JokerVariant } : {}),
              })}
            >
              <option value="">Natural configured variant</option>
              {props.feasible.map(item => (
                <option value={item.variant} key={item.variant}>{JOKER_LABELS[item.variant]}</option>
              ))}
            </select>
          </label>
          {selected && (
            <label>
              Target cell
              <select
                aria-label={`${props.label} Joker target`}
                value={props.control?.target ? cellKey(props.control.target) : ''}
                onChange={event => {
                  const target = selected.targets.find(cell => cellKey(cell) === event.target.value);
                  props.onChange({
                    version: 1,
                    activation: 'activate',
                    variant: selected.variant,
                    ...(target ? { target } : {}),
                    ...(supportsPrize && props.control?.prizeMultiplier !== undefined
                      ? { prizeMultiplier: props.control.prizeMultiplier }
                      : {}),
                  });
                }}
              >
                <option value="">Natural eligible target</option>
                {selected.targets.map(cell => (
                  <option value={cellKey(cell)} key={cellKey(cell)}>R{cell.reel + 1} · {cell.row + 1}</option>
                ))}
              </select>
            </label>
          )}
          {supportsPrize && (
            <label>
              Prize Coin value
              <select
                aria-label={`${props.label} Joker Prize multiplier`}
                value={props.control?.prizeMultiplier ?? ''}
                onChange={event => props.onChange({
                  ...props.control!,
                  version: 1,
                  activation: 'activate',
                  variant: selected!.variant,
                  ...(event.target.value ? { prizeMultiplier: Number(event.target.value) } : { prizeMultiplier: undefined }),
                })}
              >
                <option value="">Natural configured value</option>
                {getConfiguredPrizeMultipliers(props.config).map(value => (
                  <option value={value} key={value}>{value}x</option>
                ))}
              </select>
            </label>
          )}
        </>
      )}
    </fieldset>
  );
};

const outcomeLabel = (outcome: string, cell: TFf036BoardCell, increment: number) => {
  if (outcome === 'nothing') return 'Nothing';
  if (outcome === 'prize-coin') return 'Prize Coin';
  if (outcome === 'fixed-jackpot-coin') return 'Fixed jackpot';
  if (outcome === 'vortex') return 'Vortex';
  return cell.occupantType === null ? `Stored +${increment}x multiplier` : `Immediate +${increment}x multiplier`;
};

const FeatureCard = (props: {
  index: number;
  card: TFf036FeatureCard;
  boardBefore: TFf036Board;
  boardAfter: TFf036Board;
  stepAfter: TFf036FeatureStep;
  config: TFf036Config;
  onChange: (card: TFf036FeatureCard) => void;
}) => {
  const [selected, setSelected] = React.useState({ reel: 0, row: 0 });
  const selectedEvent = props.card.events.find(event => cellKey(event) === cellKey(selected));
  const naturalBoard = applyCard(props.boardBefore, props.card, props.config);
  const feasibleJokers = getHoldJokerFeasibleVariants(naturalBoard, props.config);
  const boardBeforeSelection = props.card.events
    .filter(event => cellOrder(event) < cellOrder(selected))
    .reduce((board, event) => applyEvent(board, event, props.config), clone(props.boardBefore));
  const currentCell = boardBeforeSelection[selected.reel][selected.row];
  const canAdd = canAddEventAt(props.card, selected);
  const outcomes = canAdd ? getValidOutcomes(currentCell, props.config) : ['nothing' as const];
  const selectedOutcome = selectedEvent?.type || 'nothing';

  const updateOutcome = (outcome: typeof outcomes[number], value?: number | TFf036Jackpot) => {
    props.onChange(addOrReplaceEvent({
      card: props.card,
      cardIndex: props.index,
      boardBeforeCard: props.boardBefore,
      cell: selected,
      outcome,
      value,
      config: props.config,
    }));
  };

  return (
    <section className="ff036-feature-card">
      <h3>Coin Feature Spin {props.index + 1}</h3>
      <div className="ff036-spin-status">
        {props.stepAfter.terminalReason === 'active'
          ? `${props.stepAfter.respinsRemaining} respin(s) remaining`
          : 'Feature complete · respins exhausted'}
      </div>
      <FeatureBoard board={props.boardAfter} selected={selected} onSelect={setSelected} />
      <div className="ff036-cell-editor">
        <strong>Cell R{selected.reel + 1} · {selected.row + 1}</strong>
        <select
          aria-label={`Outcome for feature ${props.index} cell ${selected.reel}-${selected.row}`}
          value={selectedOutcome}
          onChange={event => updateOutcome(event.target.value as typeof outcomes[number])}
        >
          {outcomes.map(outcome => (
            <option value={outcome} key={outcome}>
              {outcomeLabel(outcome, currentCell, props.config.coinFeature.multiplierIncrement)}
            </option>
          ))}
        </select>
        {selectedOutcome === 'prize-coin' && (
          <select
            aria-label={`Prize value for feature ${props.index} cell ${selected.reel}-${selected.row}`}
            value={selectedEvent?.multiplier}
            onChange={event => updateOutcome('prize-coin', Number(event.target.value))}
          >
            {getConfiguredPrizeMultipliers(props.config).map(value => <option value={value} key={value}>{value}x</option>)}
          </select>
        )}
        {selectedOutcome === 'fixed-jackpot-coin' && (
          <select
            aria-label={`Jackpot for feature ${props.index} cell ${selected.reel}-${selected.row}`}
            value={selectedEvent?.jackpotType}
            onChange={event => updateOutcome('fixed-jackpot-coin', event.target.value as TFf036Jackpot)}
          >
            {getConfiguredJackpots(props.config).map(value => <option value={value} key={value}>{value.toUpperCase()}</option>)}
          </select>
        )}
        {!canAdd && <span className="ff036-order-note">Choose cells in reel-major order for this spin.</span>}
      </div>
      <ol className="ff036-event-order" aria-label={`Selected event order for feature ${props.index}`}>
        {props.card.events.map(event => (
          <li key={event.id}>{event.type} · R{event.reel + 1}/{event.row + 1}{event.multiplier ? ` · ${event.multiplier}x` : event.jackpotType ? ` · ${event.jackpotType.toUpperCase()}` : ''}</li>
        ))}
      </ol>
      <JokerControls
        label={`Hold & Win ${props.index + 1}`}
        control={props.card.jokerIntervention}
        feasible={feasibleJokers}
        config={props.config}
        onChange={jokerIntervention => props.onChange({
          ...props.card,
          ...(jokerIntervention
            ? { jokerIntervention: preserveUnknownJokerFields(props.card.jokerIntervention, jokerIntervention) }
            : { jokerIntervention: undefined }),
        })}
      />
      {props.stepAfter.terminalReason !== 'active' && (
        <div className="ff036-terminal">
          Three consecutive empty spins · feature completes normally
        </div>
      )}
    </section>
  );
};

export const Model = ({ modelId }: { modelId: string }) => {
  const [config, setConfig] = React.useState<TFf036Config>();
  const [state, setState] = React.useState<TFf036EditorState>();
  const [presets, setPresets] = React.useState<TPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = React.useState('');
  const [userId, setUserId] = React.useState(localStorage.getItem('userid') || '');
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([
      request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get-config`)
        .withCredentials().send({ modelId }),
      request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get`)
        .withCredentials().send({ modelId }),
    ]).then(([configRes, scenariosRes]) => {
      if (cancelled) return;
      if (!configRes.body?.success && !configRes.body?.config) {
        throw new Error(responseErrorMessage(configRes.body, 'Error loading FF036 config'));
      }
      if (!scenariosRes.body?.success) {
        throw new Error(responseErrorMessage(scenariosRes.body, 'Error loading FF036 scenarios'));
      }
      const loadedConfig = configRes.body.config as TFf036Config;
      setConfig(loadedConfig);
      setPresets(scenariosRes.body.scenarios || []);
      setState(createBlankState(loadedConfig));
    }).catch(caught => {
      if (!cancelled) setError(requestErrorMessage(caught, 'Error loading FF036 editor'));
    });
    return () => { cancelled = true; };
  }, [modelId]);

  if (!config || !state) return <>{error ? <div role="alert">{error}</div> : 'Loading'}</>;

  const queue = serializeScenarioQueue(state);
  const visibleWindow = getVisibleWindow(state.base.reelIndexes, config);
  const unsupported = !!state.unsupportedQueue;
  const triggersHold = !unsupported && editorTriggersHold(state, config);
  let featureSteps: TFf036FeatureStep[] = [];
  let projectionError = '';
  if (state.base.superPsychoRespin?.activation === 'activate' && state.superContinuation &&
    !superContinuationSatisfiesAward(state, config)) {
    projectionError = `The Super Psycho continuation does not satisfy the selected ${state.base.superPsychoRespin.awardClass} award class.`;
  } else if (triggersHold) {
    try {
      featureSteps = projectFeatureSteps(state, config);
    } catch (caught) {
      projectionError = caught instanceof Error ? caught.message : 'The Hold & Win queue cannot be projected safely.';
    }
  }
  const lastFeatureStep = featureSteps[featureSteps.length - 1];
  const featureTerminal = lastFeatureStep?.terminalReason !== 'active';
  const baseJokerFeasible = getBaseJokerFeasibleVariants(state.base, config);
  const baseGrowthEligible = getBaseGrowthEligibleCells(state.base, config);
  const superWindow = state.superContinuation && state.base.superPsychoRespin?.awardClass
    ? getSuperContinuationWindow(state, config) : undefined;
  const superGrowthEligible: Array<{ reel: number; row: number }> = [];
  const superForceEligible = isSuperPsychoForceEligible(state.base, config);
  const superOwnsFeatureSeed = state.base.superPsychoRespin?.activation === 'activate' &&
    state.base.superPsychoRespin.awardClass === 'hold-and-win' && !!state.superContinuation;
  const baseCoinValuesForced = baseCoinValuesAreForced(state.base);
  const holdQueueEditable = holdSeedValuesAreForced(state);

  const findBaseOutcome = (kind: TFf036SearchKind) => {
    const reelIndexes = findReelIndexes(config, kind, state.base.reelIndexes);
    if (!reelIndexes) {
      setError('No matching outcome can be found on the configured reels.');
      return;
    }
    const base = reconcileBaseCoins({
      ...state.base,
      version: 2,
      reelIndexes,
      coinOverlays: [],
      coinOverlaysPresent: true,
      randomBonus: kind === 'random-trigger' ? 'activate' : 'skip',
      superPsychoRespin: { activation: 'skip' },
      jokerIntervention: { version: 1, activation: 'skip' },
      perceivedGrowthIncrementCells: undefined,
    }, config);
    setState({ ...state, base, superContinuation: undefined, featureCards: [] });
    setError('');
    setNotice(kind === 'random-trigger'
      ? 'Found fewer than 3 special symbols; random bonus completion is forced.'
      : kind === 'losing' ? 'Found a losing spin eligible for Super Psycho Respin.'
        : 'Found a natural Coin Spins trigger. Click again to find the next one.');
  };

  const changeRandomBonus = (randomBonus: 'activate' | 'skip') => {
    setState(current => {
      if (!current) return current;
      const base = reconcileBaseCoins({
        ...current.base,
        version: 2,
        randomBonus,
        ...(randomBonus === 'activate' ? {
          coinOverlaysPresent: true,
          superPsychoRespin: { activation: 'skip' as const },
        } : {}),
      }, config);
      const candidate = { ...current, base,
        superContinuation: randomBonus === 'activate' ? undefined : current.superContinuation, featureCards: [] };
      return reconcileFeatureEvidence(candidate, config);
    });
  };

  const setReelIndex = (reel: number, value: number) => {
    const strip = config.baseSpinsReels[reel];
    const wrapped = wrap(value, strip);
    const normalized = wrapped < 0 ? strip.length + wrapped : wrapped;
    setState(current => {
      if (!current) return current;
      const base = reconcileBaseCoins({
        ...current.base,
        reelIndexes: current.base.reelIndexes.map((old, index) => index === reel ? normalized : old),
      }, config);
      base.jokerIntervention = reconcileJokerControl(
        base.jokerIntervention,
        getBaseJokerFeasibleVariants(base, config),
        config,
      );
      if (base.perceivedGrowthIncrementCells !== undefined) {
        base.perceivedGrowthIncrementCells = reconcileGrowthCells(
          base.perceivedGrowthIncrementCells,
          getBaseGrowthEligibleCells(base, config),
          config,
        );
      }
      if (base.randomBonus === 'activate' && !isRandomBonusEligible(base, config)) base.randomBonus = 'skip';
      const superInvalid = base.superPsychoRespin?.activation === 'activate' &&
        !isSuperPsychoForceEligible(base, config);
      if (superInvalid) base.superPsychoRespin = { activation: 'skip' };
      const candidate = {
        ...current,
        base,
        ...(superInvalid ? { superContinuation: undefined } : {}),
      };
      const next = {
        ...candidate,
        featureCards: editorTriggersHold(candidate, config) && holdSeedValuesAreForced(candidate)
          ? current.featureCards
          : [],
      };
      return reconcileFeatureEvidence(next, config);
    });
  };

  const changeBaseCoinBehavior = (value: 'natural' | 'force') => {
    setState(current => {
      if (!current) return current;
      const base = reconcileBaseCoins({
        ...current.base,
        coinOverlays: [],
        coinOverlaysPresent: value === 'force' ? true : undefined,
      }, config);
      base.jokerIntervention = reconcileJokerControl(
        base.jokerIntervention,
        getBaseJokerFeasibleVariants(base, config),
        config,
      );
      if (base.perceivedGrowthIncrementCells !== undefined) {
        base.perceivedGrowthIncrementCells = reconcileGrowthCells(
          base.perceivedGrowthIncrementCells,
          getBaseGrowthEligibleCells(base, config),
          config,
        );
      }
      if (base.randomBonus === 'activate' && !isRandomBonusEligible(base, config)) base.randomBonus = 'skip';
      const superInvalid = base.superPsychoRespin?.activation === 'activate' &&
        !isSuperPsychoForceEligible(base, config);
      if (superInvalid) base.superPsychoRespin = { activation: 'skip' };
      const candidate = {
        ...current,
        base,
        ...(superInvalid ? { superContinuation: undefined } : {}),
      };
      return reconcileFeatureEvidence({
        ...candidate,
        featureCards: editorTriggersHold(candidate, config) && holdSeedValuesAreForced(candidate)
          ? current.featureCards
          : [],
      }, config);
    });
  };

  const changeBaseCoin = (cell: { reel: number; row: number }, value: string) => {
    const parsed = Number(value);
    const overlay: TFf036CoinOverlay = Number.isFinite(parsed)
      ? { ...cell, coinType: 'price', multiplier: parsed }
      : { ...cell, coinType: value as TFf036Jackpot };
    setState(current => {
      if (!current) return current;
      const base: typeof current.base = {
        ...current.base,
        coinOverlaysPresent: true,
        coinOverlays: current.base.coinOverlays.some(coin => cellKey(coin) === cellKey(cell))
          ? current.base.coinOverlays.map(coin => cellKey(coin) === cellKey(cell) ? overlay : coin)
          : [...current.base.coinOverlays, overlay],
      };
      base.jokerIntervention = reconcileJokerControl(
        base.jokerIntervention,
        getBaseJokerFeasibleVariants(base, config),
        config,
      );
      if (base.randomBonus === 'activate' && !isRandomBonusEligible(base, config)) base.randomBonus = 'skip';
      const superInvalid = base.superPsychoRespin?.activation === 'activate' &&
        !isSuperPsychoForceEligible(base, config);
      if (superInvalid) base.superPsychoRespin = { activation: 'skip' };
      const candidate = {
        ...current,
        base,
        ...(superInvalid ? { superContinuation: undefined } : {}),
      };
      return reconcileFeatureEvidence({
        ...candidate,
        featureCards: editorTriggersHold(candidate, config) && holdSeedValuesAreForced(candidate)
          ? current.featureCards
          : [],
      }, config);
    });
  };

  const changeBaseJoker = (jokerIntervention?: TFf036JokerControl) => {
    setState(current => {
      if (!current) return current;
      const nextJoker = preserveUnknownJokerFields(current.base.jokerIntervention, jokerIntervention);
      const base = { ...current.base, jokerIntervention: nextJoker, version: nextJoker ? 2 as const : current.base.version };
      const reconciled = reconcileJokerControl(nextJoker, getBaseJokerFeasibleVariants(base, config), config);
      base.jokerIntervention = reconciled;
      if (base.randomBonus === 'activate' && !isRandomBonusEligible(base, config)) base.randomBonus = 'skip';
      if (reconciled?.activation === 'activate' && base.superPsychoRespin?.activation === 'activate') {
        base.superPsychoRespin = { activation: 'skip' };
      }
      if (base.perceivedGrowthIncrementCells !== undefined) {
        base.perceivedGrowthIncrementCells = reconcileGrowthCells(
          base.perceivedGrowthIncrementCells,
          getBaseGrowthEligibleCells(base, config),
          config,
        );
      }
      const candidate = {
        ...current,
        base,
        ...(base.superPsychoRespin?.activation === 'activate' ? {} : { superContinuation: undefined }),
      };
      return reconcileFeatureEvidence({
        ...candidate,
        featureCards: editorTriggersHold(candidate, config) && holdSeedValuesAreForced(candidate)
          ? current.featureCards
          : [],
      }, config);
    });
  };

  const changeSuperActivation = (value: 'natural' | 'activate' | 'skip') => {
    const initialAward = value === 'activate'
      ? (['line', 'collect', 'hold-and-win'] as const).find(award => findReelIndexes(config, award))
      : undefined;
    if (value === 'activate' && !initialAward) {
      setError('No Psycho award can be found on the configured reels.');
      return;
    }
    setState(current => {
      if (!current) return current;
      if (value === 'natural' || value === 'skip') {
        const base = {
          ...current.base,
          superPsychoRespin: value === 'skip' ? { activation: 'skip' as const } : undefined,
        };
        const candidate = { ...current, base, superContinuation: undefined };
        return reconcileFeatureEvidence({
          ...candidate,
          featureCards: editorTriggersHold(candidate, config) && holdSeedValuesAreForced(candidate)
            ? current.featureCards
            : [],
        }, config);
      }
      const base = {
        ...current.base,
        version: config.jokerIntervention?.enabled ? 2 as const : current.base.version,
        superPsychoRespin: { activation: 'activate' as const, awardClass: initialAward! },
        ...(config.jokerIntervention?.enabled
          ? { jokerIntervention: preserveUnknownJokerFields(current.base.jokerIntervention, {
            version: 1 as const,
            activation: 'skip' as const,
          }) }
          : {}),
      };
      return { ...current, base, superContinuation: createSuperContinuation(base, config), featureCards: [] };
    });
  };

  const changeSuperAward = (awardClass?: 'line' | 'collect' | 'hold-and-win') => {
    if (awardClass && !findReelIndexes(config, awardClass)) {
      setError(`No ${awardClass} award can be found on the configured reels.`);
      return;
    }
    setState(current => {
      if (!current) return current;
      const base = {
        ...current.base,
        superPsychoRespin: {
          activation: 'activate' as const,
          ...(awardClass ? { awardClass } : {}),
        },
      };
      const superContinuation = createSuperContinuation(base, config);
      return { ...current, base, superContinuation, featureCards: [] };
    });
  };

  const updateSuperContinuation = (update: (value: NonNullable<TFf036EditorState['superContinuation']>) => NonNullable<TFf036EditorState['superContinuation']>) => {
    setState(current => {
      if (!current?.superContinuation) return current;
      const superContinuation = reconcileSuperContinuation(update(current.superContinuation), current, config);
      const candidate = { ...current, superContinuation };
      const naturalSuperSeed = candidate.base.superPsychoRespin?.awardClass === 'hold-and-win' &&
        (!superContinuation.coinOverlaysPresent || !superContinuation.reelIndexes);
      return reconcileFeatureEvidence({
        ...candidate,
        featureCards: editorTriggersHold(candidate, config) && !naturalSuperSeed ? current.featureCards : [],
      }, config);
    });
  };

  const showError = (message: string) => { setNotice(''); setError(message); };
  const showNotice = (message: string) => { setError(''); setNotice(message); };
  const payload = () => ({ modelId, scenarios: serializeScenarioQueue(state) });

  const save = async () => {
    try {
      const body = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/upsert`)
        .withCredentials().send({ ...payload(), _id: selectedPreset || undefined })).body;
      if (!body?.success) return showError(responseErrorMessage(body, 'Error saving scenario'));
      if (body._id) setSelectedPreset(body._id);
      showNotice('Scenario saved');
    } catch (caught) {
      showError(requestErrorMessage(caught, 'Error saving scenario'));
    }
  };

  const del = async () => {
    if (!selectedPreset || !window.confirm('Confirm deletion')) return;
    try {
      const body = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/delete`)
        .withCredentials().send({ modelId, _id: selectedPreset })).body;
      if (!body?.success) return showError(responseErrorMessage(body, 'Error deleting scenario'));
      setPresets(current => current.filter(preset => preset._id !== selectedPreset));
      setSelectedPreset('');
      setState(createBlankState(config));
      showNotice('Scenario deleted');
    } catch (caught) {
      showError(requestErrorMessage(caught, 'Error deleting scenario'));
    }
  };

  const play = async () => {
    if (!userId.trim()) return showError('Please enter a user id');
    try {
      const body = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/play-user`)
        .withCredentials().send({
          ...payload(),
          gameId: modelId,
          userPublicKey: userId,
        })).body;
      if (!body?.success) return showError(responseErrorMessage(body, 'Error queueing scenario'));
      showNotice('Scenario queued for play');
    } catch (caught) {
      showError(requestErrorMessage(caught, 'Error queueing scenario'));
    }
  };

  const selectPreset = (id: string) => {
    setSelectedPreset(id);
    setError('');
    setNotice('');
    const preset = presets.find(item => item._id === id);
    if (preset) setState(deserializeScenarioQueue(clone(preset.scenarios), config));
  };

  return (
    <main className="ff001-container ff036-container">
      <h1>{modelId}</h1>
      <div className="scenario-presets">
        <h3>Scenario presets</h3>
        <select aria-label="FF036 scenario preset" value={selectedPreset} onChange={event => selectPreset(event.target.value)}>
          <option value="" />
          {presets.map(preset => <option value={preset._id} key={preset._id}>{preset._id}</option>)}
        </select>
      </div>

      {unsupported && (
        <div className="ff036-message error" role="alert">
          {state.unsupportedReason} This queue is preserved exactly and is read-only in the visual editor.
        </div>
      )}
      {!unsupported && <section className="spin ff036-base-spin">
        <h2>Base Spin</h2>
        <div className="btn-freespins-group">
          <button type="button" className="btn-freespins-action" onClick={() => findBaseOutcome('natural-trigger')}>Find natural Coin Spins trigger</button>
          <button type="button" className="btn-freespins-action" onClick={() => findBaseOutcome('random-trigger')}>Find &amp; force &lt;3-symbol trigger</button>
          <button type="button" className="btn-freespins-action" onClick={() => findBaseOutcome('losing')}>Find losing spin for Psycho</button>
        </div>
        <div className="reelContainer">
          {state.base.reelIndexes.map((reelIndex, index) => (
            <Reel
              key={index}
              index={index}
              rows={config.reelsSetup[index]}
              reelIndex={reelIndex}
              reelStrips={config.baseSpinsReels[index]}
              setReelIndex={({ reelIndex: reel, dir, forcedIndex }) => setReelIndex(
                reel,
                typeof forcedIndex === 'number' ? forcedIndex : state.base.reelIndexes[reel] + (dir || 1),
              )}
            />
          ))}
        </div>
        <BaseBoard
          window={visibleWindow}
          coinOverlays={state.base.coinOverlays}
          config={config}
          ariaLabel="FF036 Base Spin window"
          coinLabel="Base"
          coinEditingEnabled={baseCoinValuesForced}
          growthCells={config.perceivedGrowth?.enabled === false
            ? undefined
            : state.base.perceivedGrowthIncrementCells}
          growthEligibleCells={baseGrowthEligible}
          onCoinChange={changeBaseCoin}
          onGrowthToggle={cell => setState(current => current && ({
            ...current,
            base: {
              ...current.base,
              perceivedGrowthIncrementCells: toggleCell(current.base.perceivedGrowthIncrementCells || [], cell),
            },
          }))}
        />
        <fieldset className="ff036-control-panel">
          <legend>Base Coin values</legend>
          <label>
            Behavior
            <select
              aria-label="Base Coin value behavior"
              value={baseCoinValuesForced ? 'force' : 'natural'}
              onChange={event => changeBaseCoinBehavior(event.target.value as 'natural' | 'force')}
            >
              <option value="natural">Natural (server-selected)</option>
              <option value="force">Force every visible Coin</option>
            </select>
          </label>
        </fieldset>
        <fieldset className="ff036-control-panel">
          <legend>Base perceived growth</legend>
          {config.perceivedGrowth?.enabled === false ? (
            <p>Perceived growth is disabled by this configuration.</p>
          ) : (
            <label>
              Behavior
              <select
                aria-label="Base perceived growth behavior"
                value={state.base.perceivedGrowthIncrementCells === undefined ? 'natural' : 'force'}
                onChange={event => setState(current => current && ({
                  ...current,
                  base: {
                    ...current.base,
                    perceivedGrowthIncrementCells: event.target.value === 'force' ? [] : undefined,
                  },
                }))}
              >
                <option value="natural">Natural (server-selected)</option>
                <option value="force">Force selected cells</option>
              </select>
            </label>
          )}
        </fieldset>
        <fieldset className="ff036-control-panel">
          <legend>Random Coin Spins trigger</legend>
          <label>
            Behavior
            <select aria-label="Random Coin Spins trigger" value={state.base.randomBonus || 'skip'}
              onChange={event => changeRandomBonus(event.target.value as 'activate' | 'skip')}>
              <option value="skip">Skip random trigger</option>
              <option value="activate" disabled={!isRandomBonusEligible(state.base, config)}>Force trigger completion</option>
            </select>
          </label>
          <p>Requires a Coin or Vortex without a natural trigger. Adds the missing entry symbols; the perceived growth meter is visual and does not control activation.</p>
        </fieldset>
        <JokerControls
          label="Base"
          control={state.base.jokerIntervention}
          feasible={baseJokerFeasible}
          config={config}
          onChange={changeBaseJoker}
        />
        <fieldset className="ff036-control-panel ff036-super-controls">
          <legend>Super Psycho Respin</legend>
          <label>
            Behavior
            <select
              aria-label="Super Psycho Respin activation"
              value={state.base.superPsychoRespin?.activation || 'natural'}
              onChange={event => changeSuperActivation(event.target.value as 'natural' | 'activate' | 'skip')}
            >
              <option value="natural">Natural (server-selected)</option>
              <option value="activate" disabled={!superForceEligible}>Force activation</option>
              <option value="skip">Force skip</option>
            </select>
          </label>
          {!superForceEligible && state.base.superPsychoRespin?.activation !== 'activate' && (
            <p>Forced activation is unavailable because this Base outcome already awards or starts a feature.</p>
          )}
          {state.base.superPsychoRespin?.activation === 'activate' && (
            <>
              <label>
                Award class
                <select
                  aria-label="Super Psycho Respin award class"
                  value={state.base.superPsychoRespin.awardClass || ''}
                  onChange={event => changeSuperAward(event.target.value
                    ? event.target.value as 'line' | 'collect' | 'hold-and-win'
                    : undefined)}
                >
                  <option value="">Natural configured award class</option>
                  <option value="line">Line</option>
                  <option value="collect">Collect</option>
                  <option value="hold-and-win">Hold & Win</option>
                </select>
              </label>
              {!state.superContinuation && (
                <button
                  type="button"
                  className="btn-freespins-action"
                  onClick={() => setState(current => current && ({
                    ...current,
                    superContinuation: createSuperContinuation(current.base, config),
                  }))}
                >Add Super Psycho continuation</button>
              )}
              {state.superContinuation && (
                state.base.superPsychoRespin.awardClass ? (
                  <>
                    <label className="ff036-checkbox-label">
                      <input
                        type="checkbox"
                        aria-label="Customize Super Psycho continuation reels"
                        checked={!!state.superContinuation.reelIndexes}
                        onChange={event => updateSuperContinuation(value => ({
                          ...value,
                          reelIndexes: event.target.checked
                            ? findReelIndexes(config, state.base.superPsychoRespin!.awardClass!) : undefined,
                        }))}
                      />
                      Customize continuation reel indexes
                    </label>
                    <button type="button" className="btn-freespins-action" onClick={() => updateSuperContinuation(value => ({
                      ...value,
                      reelIndexes: findReelIndexes(config, state.base.superPsychoRespin!.awardClass!, value.reelIndexes),
                    }))}>Find next Psycho outcome</button>
                    {!state.superContinuation.reelIndexes && <p>Example matching window; the server selects the actual stops. Find an outcome to pin its reel indexes.</p>}
                    {state.superContinuation.reelIndexes && (
                      <div className="reelContainer" aria-label="Super Psycho continuation reels">
                        {state.superContinuation.reelIndexes.map((reelIndex, index) => (
                          <Reel
                            key={index}
                            index={index}
                            rows={config.reelsSetup[index]}
                            reelIndex={reelIndex}
                            reelStrips={config.baseSpinsReels[index]}
                            setReelIndex={({ reelIndex: reel, dir, forcedIndex }) => updateSuperContinuation(value => {
                              const strip = config.baseSpinsReels[reel];
                              const requested = typeof forcedIndex === 'number'
                                ? forcedIndex
                                : value.reelIndexes![reel] + (dir || 1);
                              const wrapped = wrap(requested, strip);
                              return {
                                ...value,
                                reelIndexes: value.reelIndexes!.map((old, current) => current === reel
                                  ? (wrapped < 0 ? strip.length + wrapped : wrapped)
                                  : old),
                              };
                            })}
                          />
                        ))}
                      </div>
                    )}
                    <fieldset className="ff036-control-panel">
                      <legend>Super continuation Coin values</legend>
                      <label>
                        Behavior
                        <select
                          aria-label="Super Coin value behavior"
                          value={state.superContinuation.coinOverlaysPresent ? 'force' : 'natural'}
                          onChange={event => updateSuperContinuation(value => ({
                            ...value,
                            coinOverlays: [],
                            coinOverlaysPresent: event.target.value === 'force' ? true : undefined,
                          }))}
                        >
                          <option value="natural">Natural (server-selected)</option>
                          <option value="force">Force every visible Coin</option>
                        </select>
                      </label>
                    </fieldset>
                    <BaseBoard
                      window={superWindow!}
                      coinOverlays={state.superContinuation.coinOverlays}
                      config={config}
                      ariaLabel="Super Psycho Respin continuation window"
                      coinLabel="Super"
                      coinEditingEnabled={!!state.superContinuation.coinOverlaysPresent}
                      growthCells={config.perceivedGrowth?.enabled === false
                        ? undefined
                        : state.superContinuation.perceivedGrowthIncrementCells}
                      growthEligibleCells={superGrowthEligible}
                      onCoinChange={(cell, value) => updateSuperContinuation(continuation => {
                        const parsed = Number(value);
                        const overlay: TFf036CoinOverlay = Number.isFinite(parsed)
                          ? { ...cell, coinType: 'price', multiplier: parsed }
                          : { ...cell, coinType: value as TFf036Jackpot };
                        return {
                          ...continuation,
                          coinOverlays: continuation.coinOverlays.some(coin => cellKey(coin) === cellKey(cell))
                            ? continuation.coinOverlays.map(coin => cellKey(coin) === cellKey(cell) ? overlay : coin)
                            : [...continuation.coinOverlays, overlay],
                        };
                      })}
                      onGrowthToggle={cell => updateSuperContinuation(continuation => ({
                        ...continuation,
                        perceivedGrowthIncrementCells: toggleCell(
                          continuation.perceivedGrowthIncrementCells || [],
                          cell,
                        ),
                      }))}
                    />
                    <fieldset className="ff036-control-panel">
                      <legend>Super continuation perceived growth</legend>
                      <p>The free continuation does not increment the meter. Entering Coin Spins resets it.</p>
                      {config.perceivedGrowth?.enabled === false ? (
                        <p>Perceived growth is disabled by this configuration.</p>
                      ) : (
                        <label>
                          Behavior
                          <select
                            aria-label="Super perceived growth behavior"
                            value={state.superContinuation.perceivedGrowthIncrementCells === undefined ? 'natural' : 'force'}
                            onChange={event => updateSuperContinuation(value => ({
                              ...value,
                              perceivedGrowthIncrementCells: event.target.value === 'force' ? [] : undefined,
                            }))}
                          >
                            <option value="natural">Natural (server-selected)</option>
                            <option value="force">Force no increment</option>
                          </select>
                        </label>
                      )}
                    </fieldset>
                  </>
                ) : (
                  <p>Continuation outcome controls stay natural while the award class is server-selected.</p>
                )
              )}
            </>
          )}
        </fieldset>
        <div className={`ff036-trigger ${triggersHold ? 'active' : ''}`}>
          {triggersHold
            ? state.base.randomBonus === 'activate'
              ? 'Hold & Win triggered: random completion adds the missing Coins and Vortex.'
              : 'Hold & Win triggered: Vortex plus Coins on both outer reels.'
            : `${getVisibleCoinCells(visibleWindow, config).length} Coin(s), ${getVisibleVortexCells(visibleWindow).length} Vortex cell(s) · no Hold & Win trigger`}
        </div>
      </section>}

      {projectionError && <div className="ff036-message error" role="alert">{projectionError}</div>}
      {triggersHold && !projectionError && (
        <section className="ff036-feature-queue">
          <h2>Hold & Win queue</h2>
          <p>Boards show the result after collection. Coins clear and can land again; collectors stay. Any new Coin, Vortex, or multiplier resets respins to 3.</p>
          {!holdQueueEditable && (
            <div className="ff036-trigger">
              Force every visible {superOwnsFeatureSeed ? 'Super continuation' : 'Base'} Coin value{superOwnsFeatureSeed ? ' and use explicit continuation reel indexes' : ''} to edit downstream Hold & Win cards.
            </div>
          )}
          {state.featureCards.map((card, index) => (
            <FeatureCard
              key={index}
              index={index}
              card={card}
              boardBefore={featureSteps[index].board}
              boardAfter={featureSteps[index + 1].board}
              stepAfter={featureSteps[index + 1]}
              config={config}
              onChange={next => setState(current => current && reconcileFeatureEvidence({
                ...current,
                featureCards: current.featureCards.map((value, cardIndex) => cardIndex === index ? next : value),
              }, config))}
            />
          ))}
          <div className="btn-freespins-group">
            <button
              type="button"
              className="btn-freespins-action"
              disabled={featureTerminal || !holdQueueEditable}
              onClick={() => setState(current => current && ({
                ...current,
                featureCards: featureTerminal
                  ? current.featureCards
                  : [...current.featureCards, { events: [] }],
              }))}
            >Add Coin Feature Spin</button>
            <button
              type="button"
              className="btn-freespins-action"
              disabled={state.featureCards.length === 0}
              onClick={() => setState(current => current && ({
                ...current,
                featureCards: current.featureCards.slice(0, -1),
              }))}
            >Remove Coin Feature Spin</button>
          </div>
        </section>
      )}

      <details className="ff036-json-preview">
        <summary>Read-only native JSON preview</summary>
        <textarea aria-label="Native FF036 scenario JSON" readOnly value={JSON.stringify(queue, null, 2)} />
      </details>

      <div className="ff001-userid">
        <h3>User Public Key</h3>
        <input aria-label="User Public Key" value={userId} onChange={event => {
          setUserId(event.target.value);
          localStorage.setItem('userid', event.target.value);
        }} />
      </div>
      {error && <div className="ff036-message error" role="alert">{error}</div>}
      {notice && <div className="ff036-message success" role="status">{notice}</div>}
      <div className="model-buttons">
        <button className="btn-save" type="button" onClick={() => {
          setSelectedPreset('');
          setState(createBlankState(config));
          setError('');
          setNotice('');
        }}>New</button>
        <button className="btn-save" type="button" disabled={!!projectionError} onClick={save}>Save</button>
        {selectedPreset && <button className="btn-save" type="button" onClick={del}>Delete</button>}
        <button className="btn-save btn-play" type="button" disabled={!!projectionError} onClick={play}>Play</button>
      </div>
    </main>
  );
};

export default Model;
